import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useSelector } from 'react-redux';

import authSelectors from '@/redux/auth/authSelectors';
import appSelectors from '@/redux/app/appSelectors';

import RouteNames from '@/router/routes';

import SharedLayout from '@/components/sharedLayout';
import LoaderIndicator from '@/components/loaderIndicator';

// AUTH
const Signup = lazy(() => import('@/pages/auth/signup'));
const Login = lazy(() => import('@/pages/auth/login'));
const ForgotPassword = lazy(() => import('@/pages/auth/forgotPassword'));
const PasswordReset = lazy(() => import('@/pages/auth/passwordReset'));

// FOOTER
// const PublicOffer = lazy(() => import('@/pages/common/publicOffer'));
// const PrivacyPolicy = lazy(() => import('@/pages/common/privacyPolicy'));

// ACCOUNT
const Home = lazy(() => import('@/pages/acc/home'));
const PersonalAccounts = lazy(() => import('@/pages/acc/personalAccounts'));
const CreatePersAccount = lazy(
  () => import('@/pages/acc/createPersonalAccount'),
);
const TradeAccounts = lazy(() => import('@/pages/acc/tradeAccounts'));
const CreateTradeAccount = lazy(() => import('@/pages/acc/createTradeAccount'));
const DemoAccounts = lazy(() => import('@/pages/acc/demoAccounts'));
const CreateDemoAccount = lazy(() => import('@/pages/acc/createDemoAccount'));
const Account = lazy(() => import('@/pages/acc/account'));
const Deposit = lazy(() => import('@/pages/acc/deposit'));
const CreateDeposit = lazy(() => import('@/pages/acc/createDeposit'));
const Withdrawal = lazy(() => import('@/pages/acc/withdrawal'));
const CreateWithdrawal = lazy(() => import('@/pages/acc/createWithdrawal'));
const Transfer = lazy(() => import('@/pages/acc/transfer'));
const CreateTransfer = lazy(() => import('@/pages/acc/createTransfer'));
const PaymentHistory = lazy(() => import('@/pages/acc/paymentHistory'));
const Statistic = lazy(() => import('@/pages/acc/partnerStatistic'));
const ProgramDetails = lazy(() => import('@/pages/acc/programDetails'));
const Utm = lazy(() => import('@/pages/acc/utm'));
const CreatePartAccount = lazy(
  () => import('@/pages/acc/createPartnerAccount'),
);
const Transactions = lazy(() => import('@/pages/acc/partnerTransactions'));
const Commissions = lazy(() => import('@/pages/acc/partnerCommissions'));
const Referral = lazy(() => import('@/pages/acc/referral'));
const TradingPlatforms = lazy(() => import('@/pages/acc/tradingPlatforms'));
const Pamm = lazy(() => import('@/pages/acc/pamm'));
const Support = lazy(() => import('@/pages/acc/support'));
const CreateTicket = lazy(() => import('@/pages/acc/createTicket'));
const Ticket = lazy(() => import('@/pages/acc/ticket'));
const Notifications = lazy(() => import('@/pages/acc/notifications'));
const PersonalData = lazy(() => import('@/pages/acc/personalData'));
const FinancialProfile = lazy(() => import('@/pages/acc/financialProfile'));
const Documents = lazy(() => import('@/pages/acc/documents'));
const Settings = lazy(() => import('@/pages/acc/settings'));

const authRoutes = (
  <>
    <Route path={RouteNames.LOGIN} element={<Login />} />
    <Route path={RouteNames.SIGNUP} element={<Signup />} />
    <Route path={RouteNames.FORGOT_PASSWORD} element={<ForgotPassword />} />
    <Route path={RouteNames.PASSWORD_RESET} element={<PasswordReset />} />
  </>
);

const commonRoutes = (
  <>
    {/* <Route path={RouteNames.OFFER} element={<PublicOffer />} />
    <Route path={RouteNames.POLICY} element={<PrivacyPolicy />} /> */}
  </>
);

const accRoutes = (
  <>
    <Route index element={<Home />} />

    <Route path={RouteNames.MY_ACCOUNTS}>
      <Route index element={<Navigate to={RouteNames.PERSONAL_ACCOUNTS} />} />
      <Route path={RouteNames.PERSONAL_ACCOUNTS}>
        <Route index element={<PersonalAccounts />} />
        <Route
          path={RouteNames.CREATE_PERSONAL_ACCOUNT}
          element={<CreatePersAccount />}
        />
        <Route path={RouteNames.ACCOUNT} element={<Account />} />
      </Route>
      <Route path={RouteNames.TRADE_ACCOUNTS}>
        <Route index element={<TradeAccounts />} />
        <Route
          path={RouteNames.CREATE_TRADE_ACCOUNT}
          element={<CreateTradeAccount />}
        />
        <Route path={RouteNames.ACCOUNT} element={<Account />} />
      </Route>
      <Route path={RouteNames.DEMO_ACCOUNTS}>
        <Route index element={<DemoAccounts />} />
        <Route
          path={RouteNames.CREATE_DEMO_ACCOUNT}
          element={<CreateDemoAccount />}
        />
        <Route path={RouteNames.ACCOUNT} element={<Account />} />
      </Route>
    </Route>

    <Route path={RouteNames.PAYMENTS}>
      <Route index element={<Navigate to={RouteNames.DEPOSIT} />} />
      <Route path={RouteNames.DEPOSIT}>
        <Route index element={<Deposit />} />
        <Route path={RouteNames.CREATE_DEPOSIT} element={<CreateDeposit />} />
      </Route>
      <Route path={RouteNames.WITHDRAWAL}>
        <Route index element={<Withdrawal />} />
        <Route
          path={RouteNames.CREATE_WITHDRAWAL}
          element={<CreateWithdrawal />}
        />
      </Route>
      <Route path={RouteNames.TRANSFER}>
        <Route index element={<Transfer />} />
        <Route path={RouteNames.CREATE_TRANSFER} element={<CreateTransfer />} />
      </Route>
      <Route path={RouteNames.PAYMENT_HISTORY} element={<PaymentHistory />} />
    </Route>

    <Route path={RouteNames.PARTNERS}>
      <Route index element={<Navigate to={RouteNames.STATISTIC} />} />
      <Route path={RouteNames.STATISTIC}>
        <Route index element={<Statistic />} />
        <Route path={RouteNames.UTM} element={<Utm />} />
        <Route path={RouteNames.PROGRAM_DETAILS} element={<ProgramDetails />} />
      </Route>
      <Route path={RouteNames.COMMISSIONS}>
        <Route index element={<Commissions />} />
        <Route path={RouteNames.REFERRAL} element={<Referral />} />
      </Route>
      <Route path={RouteNames.TRANSACTIONS} element={<Transactions />} />
      <Route
        path={RouteNames.CREATE_PARTNER_ACCOUNT}
        element={<CreatePartAccount />}
      />
    </Route>

    <Route path={RouteNames.TRADING_PLATFORMS} element={<TradingPlatforms />} />
    <Route path={RouteNames.PAMM} element={<Pamm />} />

    <Route path={RouteNames.SUPPORT}>
      <Route index element={<Support />} />
      <Route path={RouteNames.CREATE_TICKET} element={<CreateTicket />} />
      <Route path={RouteNames.TICKET} element={<Ticket />} />
    </Route>

    <Route path={RouteNames.NOTIFICATIONS} element={<Notifications />} />

    <Route path={RouteNames.PROFILE}>
      <Route index element={<Navigate to={RouteNames.PERSONAL_DATA} />} />
      <Route path={RouteNames.PERSONAL_DATA} element={<PersonalData />} />
      <Route
        path={RouteNames.FINANCIAL_PROFILE}
        element={<FinancialProfile />}
      />
      <Route path={RouteNames.DOCUMENTS} element={<Documents />} />
      <Route path={RouteNames.SETTINGS} element={<Settings />} />
    </Route>
  </>
);

const publicRoutes = (
  <>
    <Route path={RouteNames.HOME} element={<SharedLayout />}>
      <Route index element={<Navigate to={RouteNames.LOGIN} replace />} />
      {authRoutes}
      {commonRoutes}
    </Route>
    <Route
      path={RouteNames.ANY}
      element={<Navigate to={RouteNames.HOME} replace />}
    />
  </>
);

const privateRoutes = (
  <>
    <Route path={RouteNames.HOME} element={<SharedLayout />}>
      {accRoutes}
      {commonRoutes}
    </Route>
    <Route
      path={RouteNames.ANY}
      element={<Navigate to={RouteNames.HOME} replace />}
    />
  </>
);

function Router() {
  const isAuthorized = useSelector(authSelectors.isAuthorized);
  const displayLoader = useSelector(appSelectors.displayLoader);

  const routes = isAuthorized ? privateRoutes : publicRoutes;

  return (
    <>
      <Suspense fallback={<LoaderIndicator />}>
        <Routes>{routes}</Routes>
      </Suspense>
      {displayLoader && <LoaderIndicator />}
    </>
  );
}

export default Router;
